import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  X, Phone, MapPin, Target, Repeat, ShieldPlus, Banknote,
  Check, PhoneOff, PhoneCall, CalendarClock, ThumbsUp, ThumbsDown, Loader2, Contact, ShieldCheck, FileSignature,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { loanProfilingApi, loansApi } from '@/services/api';
import { CompletenessRing, CibilBadge } from '@/components/loans/cockpit/CockpitUI';
import { PurposeStep, ExistingLoanStep } from '@/components/loans/cockpit/ProfileSteps';
import { VehicleCaptureBlock } from '@/components/loans/cockpit/VehicleCapture';
import { LoanAmountBlock } from '@/components/loans/cockpit/LoanAmountBlock';
import { CustomerDetailsBlock } from '@/components/loans/cockpit/CustomerDetails';
import { ApplicationAnalysis } from '@/components/loans/cockpit/ApplicationAnalysis';
import { WarrantyServiceOffers } from '@/components/loans/cockpit/WarrantyServiceOffers';
import { DisbursementStep } from '@/components/loans/cockpit/DisbursementStep';
import { OffersLetter } from '@/components/loans/cockpit/OffersLetter';

const BT_PURPOSES = ['BALANCE_TRANSFER', 'TOP_UP', 'BT_PLUS_TOPUP'];

const ALL_STEPS = [
  { key: 'purpose', label: 'Loan & Vehicle Details', icon: Target },
  { key: 'customer', label: 'Customer Details', icon: Contact },
  { key: 'analysis', label: 'Application Analysis', icon: ShieldCheck },
  { key: 'offers', label: 'Offers Letter', icon: FileSignature },
  { key: 'existing', label: 'Existing Loan', icon: Repeat, conditional: true },
  { key: 'benefits', label: 'Warranty & Service Offers', icon: ShieldPlus },
  { key: 'disbursement', label: 'Disbursement', icon: Banknote },
];

export default function LoanCockpit({ leadId, onClose }) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [partnerBanks, setPartnerBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('purpose');
  const [statusBusy, setStatusBusy] = useState(false);

  const loadCockpit = useCallback(async () => {
    const res = await loanProfilingApi.cockpit(leadId);
    setData(res.data);
    return res.data;
  }, [leadId]);

  useEffect(() => {
    (async () => {
      try {
        const [c, m, b] = await Promise.all([
          loanProfilingApi.cockpit(leadId),
          loanProfilingApi.meta(),
          loanProfilingApi.partnerBanks(),
        ]);
        setData(c.data);
        setMeta(m.data);
        setPartnerBanks(b.data.banks || []);
      } catch (err) {
        toast.error('Failed to load cockpit');
      } finally {
        setLoading(false);
      }
    })();
  }, [leadId]);

  const cp = data?.case_profile || {};
  const lead = data?.lead || {};
  const checklist = data?.checklist || [];
  const reqTotal = checklist.filter((c) => c.required).length;
  const reqDone = checklist.filter((c) => c.required && c.uploaded).length;

  const steps = ALL_STEPS.filter((s) => !s.conditional || BT_PURPOSES.includes(cp.loan_purpose));

  const isComplete = (key) => {
    switch (key) {
      case 'purpose': return !!cp.loan_purpose;
      // KYC + the one earning answer; the old form's "category" is gone.
      case 'customer': return !!(data?.customer_details?.personal?.pan
        && (cp.earning_profile || cp.income_category || cp.income_type));
      case 'analysis': return !!data?.application_analysis;
      case 'offers': return !!(data?.offers_summary?.accepted);
      case 'existing': return !!(data?.existing_loan?.lender_name);
      case 'benefits': return !!data?.service_benefits?.items?.some((i) => i.included);
      case 'disbursement': return !!data?.offers_summary?.disbursed;
      default: return false;
    }
  };

  const nextAction = () => {
    if (!cp.loan_purpose) return 'Pick a loan type';
    if (!cp.required_loan_amount) return 'Capture the loan amount asked';
    if (!lead.credit_score) return 'Pull the CIBIL report';
    if (!data?.application_analysis) return 'Run the application analysis';
    if (!cp.case_type || cp.case_type === 'UNKNOWN') return 'Answer the income questions';
    if (BT_PURPOSES.includes(cp.loan_purpose) && !data?.existing_loan?.lender_name) return 'Capture existing loan';
    // offers run alongside document collection — a rep chases both at once
    const offers = data?.offers_summary || {};
    if (offers.disbursed) return 'Disbursed — file complete';
    if (offers.accepted) return 'Capture the disbursement';
    if (reqTotal > 0 && reqDone < reqTotal) return `Collect ${reqTotal - reqDone} more document(s)`;
    if (!offers.count) return 'Gather the bank offers';
    if (!offers.shared) return 'Send the offer letter';
    return "Record the customer's choice";
  };

  // handlers
  // Optimistic case-profile patch: reflect the selection instantly, persist in the
  // background, then reconcile from the save response (no full cockpit reload). A
  // monotonic request id guards against out-of-order responses from rapid clicks.
  const profileReqRef = useRef(0);
  const patchProfile = async (patch) => {
    setData((prev) => (prev ? { ...prev, case_profile: { ...(prev.case_profile || {}), ...patch } } : prev));
    const reqId = ++profileReqRef.current;
    try {
      const res = await loanProfilingApi.saveCaseProfile(leadId, patch);
      if (reqId === profileReqRef.current && res?.data) {
        setData((prev) => (prev ? {
          ...prev,
          case_profile: res.data.case_profile || prev.case_profile,
          checklist: res.data.checklist ?? prev.checklist,
          completeness: res.data.completeness ?? prev.completeness,
        } : prev));
      }
    } catch (err) {
      toast.error('Save failed');
      if (reqId === profileReqRef.current) await loadCockpit();
    }
  };
  const saveExisting = async (body) => {
    await loanProfilingApi.saveExistingLoan(leadId, body);
    toast.success('Existing loan saved');
    await loadCockpit();
  };
  const setStatus = async (status, label) => {
    setStatusBusy(true);
    // The reason lives under the badge in the Loans table; a quick-set carries
    // no reason, so leaving the previous one there would misread the file.
    try { await loansApi.update(leadId, { status, status_notes: null }); toast.success(`Marked ${label}`); }
    catch (err) { toast.error('Failed'); }
    finally { setStatusBusy(false); }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-50 flex items-center justify-center" data-testid="loan-cockpit-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-50 flex flex-col" data-testid="loan-cockpit">
      {/* Header — identity, readiness and the steps, so the page below is all content */}
      <header className="bg-white border-b border-slate-200 shrink-0">
        <div className="h-14 flex items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="cockpit-close-btn"><X className="w-5 h-5" /></button>
            <div className="min-w-0">
              <h2 className="font-bold text-slate-900 truncate leading-tight">{lead.customer_name || 'Customer'}</h2>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                {lead.customer_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{lead.customer_phone}</span>}
                {lead.city_name && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{lead.city_name}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="hidden md:flex items-center gap-2.5" data-testid="cockpit-readiness">
              <CompletenessRing value={data?.completeness || 0} />
              <div className="leading-tight">
                <div className="text-[11px] text-slate-400">Profile readiness</div>
                <div className="text-xs font-semibold text-slate-900">{nextAction()}</div>
              </div>
            </div>
            <CibilBadge score={lead.credit_score} testId="cockpit-header-cibil" />
          </div>
        </div>

        <nav className="flex items-center gap-1 px-3 sm:px-5 overflow-x-auto" data-testid="cockpit-steps">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const done = isComplete(s.key);
            const isActive = active === s.key;
            return (
              <button key={s.key} onClick={() => setActive(s.key)} data-testid={`cockpit-step-${s.key}`}
                className={`group relative flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${isActive ? 'text-blue-700' : 'text-slate-500 hover:text-slate-800'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${done ? 'bg-emerald-500 text-white' : isActive ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'}`}>
                  {done ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                <Icon className="w-3.5 h-3.5 hidden sm:block" />
                {s.label}
                <span className={`absolute left-2 right-2 -bottom-px h-0.5 rounded-full transition-colors ${isActive ? 'bg-blue-600' : 'bg-transparent'}`} />
              </button>
            );
          })}
        </nav>
      </header>

      <main className="flex-1 overflow-y-auto" data-testid="cockpit-content">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-6">
          {active === 'purpose' && (
            <>
              <PurposeStep meta={meta} value={cp.loan_purpose} onSelect={(v) => patchProfile({ loan_purpose: v })} />
              <LoanAmountBlock
                leadId={leadId}
                caseProfile={cp}
                vehicle={(data?.profile_vehicles || []).find((v) => v.is_finalized) || (data?.profile_vehicles || [])[0] || {}}
                ltvCeilings={meta?.ltv_ceilings}
                onChanged={loadCockpit}
              />
              <VehicleCaptureBlock leadId={leadId} vehicles={data?.profile_vehicles || []} onChanged={loadCockpit} />
            </>
          )}
          {active === 'customer' && (
            <CustomerDetailsBlock leadId={leadId} lead={lead}
              personal={data?.customer_details?.personal} income={data?.customer_details?.income}
              caseProfile={cp} onPatchProfile={patchProfile} earningProfiles={meta?.earning_profiles}
              documents={data?.documents || []} checklist={checklist} onChanged={loadCockpit} />
          )}
          {active === 'analysis' && (
            <div data-testid="cockpit-analysis-tab">
              <div className="mb-4">
                <h3 className="text-lg font-semibold tracking-tight text-slate-900">Application Analysis</h3>
                <p className="text-sm text-slate-500 mt-0.5 max-w-3xl">
                  The credit manager's read on this file — bureau, repayment capacity, banking conduct and the
                  car — judged against the norms banks apply, with what it takes to clear each one.
                </p>
              </div>
              <ApplicationAnalysis leadId={leadId} onChanged={loadCockpit}
                reloadKey={`${lead?.credit_score}-${data?.case_profile?.required_loan_amount}`} />
            </div>
          )}
          {active === 'offers' && (
            <OffersLetter leadId={leadId} partnerBanks={partnerBanks}
              requiredLoanAmount={cp.required_loan_amount} onChanged={loadCockpit} />
          )}
          {active === 'existing' && <ExistingLoanStep existingLoan={data?.existing_loan} onSave={saveExisting} />}
          {active === 'benefits' && <WarrantyServiceOffers leadId={leadId} onChanged={loadCockpit} />}
          {active === 'disbursement' && <DisbursementStep leadId={leadId} onChanged={loadCockpit} />}
        </div>
      </main>

      {/* Bottom action bar */}
      <footer className="h-16 bg-white border-t border-slate-200 flex items-center justify-between px-5 shrink-0">
        <div className="text-xs text-slate-400">Lead status quick-set</div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={statusBusy} onClick={() => setStatus('INTERESTED', 'Interested')} className="text-emerald-700 hover:bg-emerald-50" data-testid="status-interested-btn"><ThumbsUp className="w-3.5 h-3.5 mr-1" />Interested</Button>
          <Button size="sm" variant="outline" disabled={statusBusy} onClick={() => setStatus('CALL_BACK', 'Call back')} data-testid="status-callback-btn"><PhoneCall className="w-3.5 h-3.5 mr-1" />Call back</Button>
          <Button size="sm" variant="outline" disabled={statusBusy} onClick={() => setStatus('FOLLOW_UP', 'Follow-up')} data-testid="status-followup-btn"><CalendarClock className="w-3.5 h-3.5 mr-1" />Follow-up</Button>
          <Button size="sm" variant="outline" disabled={statusBusy} onClick={() => setStatus('RNR', 'RNR')} data-testid="status-rnr-btn"><PhoneOff className="w-3.5 h-3.5 mr-1" />RNR</Button>
          <Button size="sm" variant="outline" disabled={statusBusy} onClick={() => setStatus('NOT_INTERESTED', 'Not interested')} className="text-rose-700 hover:bg-rose-50" data-testid="status-notinterested-btn"><ThumbsDown className="w-3.5 h-3.5 mr-1" />Not interested</Button>
          <Button size="sm" onClick={onClose} data-testid="cockpit-done-btn">Done</Button>
        </div>
      </footer>
    </div>
  );
}
